import React from 'react';
import connect from 'react-redux/es/connect/connect';
import { Table } from 'semantic-ui-react';

import { entityType as et, inforEamUrlSuffix, recordOptionType } from '../../../constants';
import { highlightComponent, isPrivateDataField } from '../../../utils';
import RecordOptions from './RecordOptions';
import OverviewTableHazardCell from './CeresCustomOverviewTableComponents/OverviewTableHazardCell';
import OverviewTableUnNumberDangerCell from './CeresCustomOverviewTableComponents/OverviewTableUnNumberDangerCell';
import { withTranslation } from 'react-i18next';
import FileDownloadableCell from './CeresCustomOverviewTableComponents/FileDownloadableCell';
import QuantityWithUnitCell from './CeresCustomOverviewTableComponents/QuantityWithUnitCell';
import TemperatureWithUnitCell from './CeresCustomOverviewTableComponents/TemperatureWithUnitCell';
import HazardStatementsCell from './CeresCustomOverviewTableComponents/HazardStatementsCell';
import DateFormattedCell from './CeresCustomOverviewTableComponents/DateFormattedCell';
import RiskCell from './CeresCustomOverviewTableComponents/RiskCell';
import ContactListCell from './CeresCustomOverviewTableComponents/ContactListCell';
import CRMCategoryCell from './CeresCustomOverviewTableComponents/CRMCategoryCell';
import UrlCell from './CeresCustomOverviewTableComponents/UrlCell';
import useDeleteActivity from '@behavior/activity/useDeleteActivity';

const OverviewTableCell = ({
  actions,
  rowValues,
  rowCount,
  displayTableOptions,
  visibleColumns,
  hazardList,
  entityTypeProps,
  handleFilterStateChanged,
  handleDefaultFilteredListOverride,
  showPersonalData,
  entityType,
  t,
}) => {
  const cells = [];
  let entityId = rowValues['ID'];
  const { deleteActivityMutate } = useDeleteActivity(entityId);
  let parentEntityId = rowValues['PARENT_ID'];
  if (displayTableOptions.length > 0) {
    cells.push(
      <Table.Cell key={'options' + rowCount} className="contentWhiteSpace tableCellOption">
        <RecordOptions
          displayTableOptions={displayTableOptions}
          handleDefaultFilteredListOverride={handleDefaultFilteredListOverride}
          entityId={entityId}
          parentEntityId={parentEntityId}
          entityCreator={rowValues['CREATOR_USERID']}
          entityResponsible={
            rowValues['CONTACTS_IDS'] !== 'null' && rowValues['CONTACTS_IDS'] !== null
              ? rowValues['CONTACTS_IDS'].split(',').map(Number)
              : []
          }
          entityDepartment={rowValues['DEPARTMENT']}
          deleteHandler={() => {
            if (entityTypeProps === et.inventory) {
              actions.deleteInventory(entityId);
            } else if (entityTypeProps === et.product) {
              actions.deleteChemicalProduct(entityId);
            } else if (entityTypeProps == et.activity) {
              deleteActivityMutate();
            }

            highlightComponent('row' + entityId, recordOptionType.delete);
            setTimeout(
              function () {
                handleFilterStateChanged();
              }.bind(this),
              2000
            );
          }}
          entityType={entityTypeProps}
        />
      </Table.Cell>
    );
  }

  visibleColumns.forEach((entry, i) => {
    let positiveCell = ['HIST_CREATE_USER', 'HIST_CREATE_DATE'].includes(entry.value);
    let negativeCell = ['GEN_DELETE_USER', 'GEN_DELETE_DATE'].includes(entry.value);

    //CERES custom table cell
    if (
      entry.value === 'GEN_PHYSICAL_HAZARD' ||
      entry.value === 'GEN_HEALTH_HAZARD' ||
      entry.value === 'GEN_ENVIRONMENTAL_HAZARD'
    ) {
      cells.push(
        <Table.Cell key={rowValues.ID + i} style={{ padding: '0px', margin: '0px' }}>
          <OverviewTableHazardCell
            rowValue={rowValues[entry.key]}
            hazardList={hazardList}
            componentKey={rowValues.ID + i}
            pictogramSize={entityTypeProps === et.building ? '50' : '28'}
          />
        </Table.Cell>
      );
    } else if (entry.value === 'CRA_RISK_LEVEL' || entry.value === 'ENV_RISK_LEVEL') {
      cells.push(
        <RiskCell key={rowValues.ID + i} rowValues={rowValues} entryRecord={entry} index={i} />
      );
    } else if (entry.value === 'BS_UN_NUMBER_DANGER_CODE') {
      cells.push(
        <Table.Cell key={rowValues.ID + i} className="tableCellReducedPadding">
          <OverviewTableUnNumberDangerCell rowValue={rowValues[entry.key]} />
        </Table.Cell>
      );
    } else if (entry.value.lastIndexOf('_DATE') !== -1) {
      cells.push(
        <DateFormattedCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          positiveCell={positiveCell}
          negativeCell={negativeCell}
          index={i}
        />
      );
    } else if (entry.value === 'CHM_HAZARD_STATEMENT') {
      cells.push(
        <HazardStatementsCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
        />
      );
    } else if (entry.value === 'GEN_QUANTITY') {
      cells.push(
        <QuantityWithUnitCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
        />
      );
    } else if (entry.value === 'CRA_BOILING_POINT' || entry.value === 'CRA_OPERATING_TEMPERATURE') {
      cells.push(
        <TemperatureWithUnitCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
        />
      );
    } else if (entry.value === 'GEN_SDS_FILES' || entry.value === 'GEN_FILES') {
      cells.push(
        <FileDownloadableCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
          icon={entry.value === 'GEN_SDS_FILES' ? 'red file pdf' : 'grey file'}
          entityType={entityType}
        />
      );
    } else if (entry.value === 'GEN_CONTACTS') {
      cells.push(
        <ContactListCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
        />
      );
    } else if (entry.value === 'MED_CMR_CLASS') {
      cells.push(
        <CRMCategoryCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
        />
      );
    } else if (entry.value === 'ENV_ASSET' || entry.value === 'ENV_FUNCTIONAL_POSITION') {
      const urlPrefix = window.REACT_APP_INFOR_EAM_URL_PATH + inforEamUrlSuffix[entry.value];
      cells.push(
        <UrlCell
          key={rowValues.ID + i}
          rowValues={rowValues}
          entryRecord={entry}
          index={i}
          urlPrefix={urlPrefix}
        />
      );
    } else {
      //Usual table cell
      cells.push(
        <Table.Cell
          key={rowValues.ID + i}
          className="tableCell tableCellReducedPadding"
          positive={positiveCell}
          negative={negativeCell}
        >
          {isPrivateDataField(entry.value) && !showPersonalData
            ? rowValues[entry.key]?.replace(/[a-z]/g, 'x')
            : rowValues[entry.key] !== 'null'
            ? t(rowValues[entry.key])
            : ''}
        </Table.Cell>
      );
    }
  });
  return cells;
};

const mapStateToProps = state => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

export default withTranslation()(connect(mapStateToProps)(OverviewTableCell));
